/**
 * @typedef {Object} TokenizedTextEditorMeta The editor metadata of the token
 * @property {string} hash The hash of the token
 * @property {string} type The type of the token
 * @property {string | undefined} altHash The alternative hash of the token
 */

class TokenizedText {
    /**
     * Generate a new TokenizedText
     *
     * @param {import("./TranslationNode")} node Parent node of the token
     * @param {number} index The index of the token
     * @param {string} text The text of the token
     * @param {{[attributeName: string]: string}} [metaExtra] The extra metadata of the token
     */
    constructor(node, index, text, metaExtra = {}) {
        /** 
         * The ID of the token
         * 
         * @type {string}
         */
        this.id = node ? node.id : "noop";
        /** 
         * The index of the token
         * 
         * @type {number}
         */
        this.index = index;
        /** 
         * The text of the token
         * 
         * @type {string}
         */
        this.text = text;
        /**
         * The meta text of the token
         * 
         * @type {{[attributeName: string]: string}}
         */
        this.metaExtra = metaExtra;
        /**
         * The editor metadata of the token
         * 
         * If null, the token is not a token with support for live editor
         * 
         * @type {TokenizedTextEditorMeta | null}
         */
        this.editorMeta = null;
        /**
         * The parent node of the token
         * 
         * @type {import("./TranslationNode")}
         */
        this.node = node;
        /**
         * Sub identifiers of the token
         * 
         * @type {string | null}
         */
        this.subId = null;
        /**
         * Extra element node type override
         * 
         * @type {string | null}
         */
        this.tagName = null;
    }

    /**
     * Check if the token is cached
     * @returns {boolean} True if the token is cached, false otherwise
     */
    isCached() {
        return this.node.cachedIndex.includes(this.index);
    }

    /**
     * Convert the token to HTML.
     * 
     * This html will not return anything if the token is translated.
     *
     * @returns {string | undefined} HTML representation of the token, or undefined if the token is translated
     */
    asHtml() {
        if (this.isCached()) {
            return;
        }

        const tagNode = this.tagName ? this.tagName : (this.node ? this.node.tagWrapper : 'div');

        /**
         * Data attributes of the token that is generated for consistency
         */
        const disallowedDataAttrs = [
            'linguiseId',
            'linguiseIndex',
            'linguiseSubId',
            'linguiseMetaExtra',
        ];

        // The base wrapper
        const stubWrapper = document.createElement('div');

        /** @type {HTMLDivElement | HTMLAnchorElement | HTMLImageElement | HTMLInputElement | HTMLTextAreaElement} */
        const node = document.createElement(tagNode);
        node.dataset.linguiseId = this.id;
        node.dataset.linguiseIndex = this.index.toString();
        if (this.subId) {
            node.dataset.linguiseSubId = this.subId;
        }

        const metaAttributes = [];
        if (this.metaExtra) {
            for (const [key, value] of Object.entries(this.metaExtra)) {
                node[key] = value;
                metaAttributes.push(key);
            }
        }

        if (metaAttributes.length > 0) {
            node.dataset.linguiseMetaExtra = metaAttributes.join(';;');
        }

        if (this.node) {
            // Pass through "class" attributes
            const nodeAttrs = this.node.element.nodeType === Node.TEXT_NODE ? this.node.element.parentElement : this.node.element.attributes;

            let currentClass = nodeAttrs.className || '';
            const nodeExtraClass = this.node.extraMetadata.class || [];
            if (nodeExtraClass.length > 0) {
                const nodeClassFilter = nodeExtraClass.filter((c) => c.trim() !== "");
                currentClass += ` ${Array.from(new Set(nodeClassFilter)).join(' ')}`;
            }

            // Passthrough class
            node.className = currentClass;

            // add ID
            if (this.node.extraMetadata.id) {
                node.id = this.node.extraMetadata.id;
            }

            if (this.node.extraMetadata.dataset) {
                const seenData = new Set();
                for (const key of Object.keys(this.node.extraMetadata.dataset)) {
                    if (seenData.has(key)) {
                        continue;
                    }
                    if (disallowedDataAttrs.indexOf(key) !== -1) {
                        continue;
                    }

                    const value = this.node.extraMetadata.dataset[key];
                    seenData.add(key);

                    node.dataset[key] = value;
                }
            }
        }

        // Add the text
        node.textContent = this.text || '';

        // Append the node to the stub wrapper
        stubWrapper.appendChild(node);

        // Get the innerHTML of the node
        return stubWrapper.innerHTML;
    }

    /** 
     * Convert back from tokenized element into proper TokenizedText
     * 
     * @param {HTMLElement | undefined} element The element to convert, or undefined if the element is not found
     */
    static fromElement(element) {
        // Check if div has <span> with data-editor="linguise"
        const spanElement = element.querySelector('span[data-editor="linguise"]');
        let textNode = element;
        if (spanElement) {
            // This is text node with editor
            textNode = spanElement;
        }

        const id = element.dataset.linguiseId;
        let index = element.dataset.linguiseIndex;

        if (!id || !index) {
            return;
        }

        index = parseInt(index);
        if (isNaN(index)) {
            return;
        }

        /**
         * Extra metadata that is not "data-linguise-" related
         *
         * @type {{[attributeName: string]: string}}
         */
        const metaExtra = {};
        if (element.dataset.linguiseMetaExtra) {
            const metaExtraKeys = element.dataset.linguiseMetaExtra.split(';;');
            for (const key of metaExtraKeys) {
                metaExtra[key] = element.getAttribute(key);
            }
        }

        const replToken = new TokenizedText(null, index, textNode.textContent, metaExtra);
        replToken.id = id;

        const subId = element.dataset.linguiseSubId;
        if (subId) {
            replToken.subId = subId;
        }

        if (element.dataset && element.dataset.editor === 'linguise') {
            const linguiseHash = element.dataset.linguiseHash;
            const linguiseType = element.dataset.linguiseType;
            const linguiseAltHash = element.dataset.linguiseAltHash;

            replToken.editorMeta = {
                hash: linguiseHash,
                type: linguiseType,
                altHash: linguiseAltHash,
            };
        }
        if (spanElement && spanElement.dataset && spanElement.dataset.editor === 'linguise') {
            const linguiseHash = spanElement.dataset.linguiseHash;

            replToken.editorMeta = {
                hash: linguiseHash,
            };
        }

        return replToken;
    }
}

export default TokenizedText;
